import { Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import useApi from 'hooks/useApi';

interface TeamCloneProps {
  teamUid: string;
  onCloned?: () => void;
}

const TeamClone = ({ teamUid, onCloned }: TeamCloneProps) => {
  const { replicateTeamByUid, showToast } = useApi();
  const [newTeamName, setNewTeamName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleClone = async () => {
    const trimmedTeamName = newTeamName.trim();

    if (!trimmedTeamName) {
      showToast('Please enter a new team name', 'warning');
      return;
    }

    try {
      setLoading(true);
      const response = await replicateTeamByUid(teamUid, {
        name: trimmedTeamName,
        newTeamName: trimmedTeamName,
        teamName: trimmedTeamName
      });
      const { success, message } = response || {};

      if (success) {
        showToast(message || 'Team cloned successfully', 'success');
        setNewTeamName('');
        onCloned?.();
      } else {
        showToast(message || 'Unable to clone team', 'error');
      }
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message;
      showToast(apiMessage || error?.message || 'Failed to clone team', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '55vh',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={72} thickness={4} />
          <Typography variant="body1" color="text.primary" sx={{ fontWeight: 700 }}>
            Cloning team...
          </Typography>
          <Typography variant="body2" color="text.primary" textAlign="center" sx={{ maxWidth: 420, fontWeight: 700 }}>
            Configuration of cloned process only contains Rules, Notification.
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Clone Process
      </Typography> 

      <Stack spacing={2} sx={{ maxWidth: 520 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' }
          }}
        >
          <Typography sx={{ minWidth: { xs: '100%', sm: 150 }, fontWeight: 500 }}>
            New Process Name
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={newTeamName}
            onChange={(event) => setNewTeamName(event.target.value)}
            disabled={loading}
            placeholder="Enter new process name"
          />
        </Box>

        <Box>
          <Button variant="contained" color="secondary"  onClick={handleClone} disabled={!newTeamName.trim()}>
            Clone Process
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default TeamClone;
